import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { IconChevronDown, IconLock } from '@tabler/icons-react';
import s from './kit.module.css';

interface FieldProps {
  label?: string;
  required?: boolean;
  hint?: ReactNode;
  error?: string;
  /** không có quyền: hiện •••••• + ổ khoá, lý do ở hint */
  locked?: boolean;
}

function Wrap({ id, label, required, hint, error, children }: FieldProps & { id: string; children: ReactNode }) {
  return (
    <div className={s.field}>
      {label && (
        <label htmlFor={id} className={s.label}>
          {label} {required && <span className={s.req}>*</span>}
        </label>
      )}
      {children}
      {error ? (
        <div id={`${id}-m`} className={s.errTx} role="alert">
          {error}
        </div>
      ) : (
        hint && (
          <div id={`${id}-m`} className={s.hint}>
            {hint}
          </div>
        )
      )}
    </div>
  );
}

export type InputProps = FieldProps & Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & { icon?: ReactNode; suffix?: ReactNode };

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, required, hint, error, locked, icon, suffix, id, className, ...rest },
  ref,
) {
  const auto = useId();
  const fid = id ?? auto;
  const box = [s.box, error && s.bad, locked && s.locked, className].filter(Boolean).join(' ');
  return (
    <Wrap id={fid} label={label} required={required} hint={hint} error={error}>
      <div className={box}>
        {icon}
        {locked ? (
          <input id={fid} className={s.input} value="••••••" readOnly aria-readonly="true" aria-describedby={error || hint ? `${fid}-m` : undefined} />
        ) : (
          <input
            ref={ref}
            id={fid}
            className={s.input}
            required={required}
            aria-invalid={error ? true : undefined}
            aria-describedby={error || hint ? `${fid}-m` : undefined}
            {...rest}
          />
        )}
        {locked ? <IconLock size={15} aria-hidden="true" /> : suffix}
      </div>
    </Wrap>
  );
});

export type TextareaProps = FieldProps & TextareaHTMLAttributes<HTMLTextAreaElement>;

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, required, hint, error, locked, id, className, rows = 3, ...rest },
  ref,
) {
  const auto = useId();
  const fid = id ?? auto;
  const box = [s.box, s.area, error && s.bad, locked && s.locked, className].filter(Boolean).join(' ');
  const desc = error || hint ? `${fid}-m` : undefined;
  return (
    <Wrap id={fid} label={label} required={required} hint={hint} error={error}>
      <div className={box}>
        {locked ? (
          <textarea id={fid} className={s.input} rows={rows} value="••••••" readOnly aria-readonly="true" aria-describedby={desc} />
        ) : (
          <textarea
            ref={ref}
            id={fid}
            className={s.input}
            rows={rows}
            required={required}
            aria-invalid={error ? true : undefined}
            aria-describedby={desc}
            {...rest}
          />
        )}
        {locked && <IconLock size={15} aria-hidden="true" />}
      </div>
    </Wrap>
  );
});

export type SelectProps = FieldProps & SelectHTMLAttributes<HTMLSelectElement> & { options: readonly (string | { value: string; label: string })[] };

/** Select thường cho danh sách ngắn (≤ 7 lựa chọn); dài hơn dùng combobox (Hybrid-Kit). */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, required, hint, error, options, id, className, ...rest },
  ref,
) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <Wrap id={fid} label={label} required={required} hint={hint} error={error}>
      <div className={[s.box, error && s.bad, className].filter(Boolean).join(' ')}>
        <select
          ref={ref}
          id={fid}
          className={s.select}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={error || hint ? `${fid}-m` : undefined}
          {...rest}
        >
          {options.map((o) => {
            const v = typeof o === 'string' ? { value: o, label: o } : o;
            return (
              <option key={v.value} value={v.value}>
                {v.label}
              </option>
            );
          })}
        </select>
        <IconChevronDown size={16} className={s.caret} aria-hidden="true" />
      </div>
    </Wrap>
  );
});
